import { AxiosError } from 'axios';
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Badge, Button, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import DatePicker from 'components/base/DatePicker';
import CustomModal from 'components/modals/CustomModal';
import { useBranchContext } from 'providers/BranchProvider';
import DataTable from '../BranchProductInventory/DataTable';
import axiosInstance from 'utils/axiosInterceptors';
import { formatDate, formatDateFilter } from 'utils/formats';
import { toastConfig } from 'utils/toastConfig';
import ExpenseForm, { ExpenseFormHandle } from './ExpenseForm';
import FundsPanel from './FundsPanel';
import UploadExpensesForm from './UploadExpensesForm';
import {
  EXPENSE_CATEGORIES,
  EXPENSE_WORKPLACES,
  OperatingExpense,
  OperatingExpenseSummary,
  formatPesos
} from './expenseOptions';

const currentMonthRange = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    dateFrom: formatDateFilter(from),
    dateTo: formatDateFilter(to)
  };
};

const formatExpenseDate = (value: string) => {
  if (!value) return '';
  return formatDate(`${value.substring(0, 10)}T00:00:00`);
};

const dash = (value?: string | null) => (value && value.trim() ? value : '—');

const isShopFundRow = (row: OperatingExpense) => row.rowKind === 'ShopFund';

const apiError = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { errorMessage?: string } | undefined;
    return data?.errorMessage || fallback;
  }
  return fallback;
};

const Expenses = () => {
  const { selectedBranch } = useBranchContext();
  const branchDomain = selectedBranch?.branchDomain;
  const initialRange = currentMonthRange();
  const [workplace, setWorkplace] = useState('');
  const [category, setCategory] = useState('');
  const [dateFrom, setDateFrom] = useState(initialRange.dateFrom);
  const [dateTo, setDateTo] = useState(initialRange.dateTo);
  const [refreshKey, setRefreshKey] = useState(0);
  const [uploadToken, setUploadToken] = useState(0);
  const [editing, setEditing] = useState<OperatingExpense | null>(null);
  const [openToken, setOpenToken] = useState(0);
  const [expenseToVoid, setExpenseToVoid] = useState<OperatingExpense | null>(
    null
  );
  const [voidReason, setVoidReason] = useState('');
  const [isVoiding, setIsVoiding] = useState(false);

  const formRef = React.useRef<ExpenseFormHandle>(null);
  const expenseModalRef = React.useRef<{ show: () => void; hide: () => void }>({
    show: () => undefined,
    hide: () => undefined
  });
  const uploadModalRef = React.useRef<{ show: () => void; hide: () => void }>({
    show: () => undefined,
    hide: () => undefined
  });
  const voidModalRef = React.useRef<{ show: () => void; hide: () => void }>({
    show: () => undefined,
    hide: () => undefined
  });

  const filters = {
    workplace: workplace || undefined,
    category: category || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined
  };

  const summaryQuery = useQuery({
    queryKey: [
      'operating-expense-summary',
      branchDomain,
      workplace,
      category,
      dateFrom,
      dateTo,
      refreshKey
    ],
    enabled: Boolean(branchDomain),
    queryFn: async () => {
      const { data } = await axiosInstance.get<OperatingExpenseSummary>(
        `/api/v1/${branchDomain}/operating-expenses/summary`,
        { params: filters }
      );
      return data;
    }
  });

  const refresh = () => setRefreshKey(key => key + 1);

  const fetchData = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    if (!branchDomain) return null;
    const { data } = await axiosInstance.get(
      `/api/v1/${branchDomain}/operating-expenses`,
      {
        params: {
          page,
          pageSize,
          search,
          orderBy,
          orderByDesc,
          ...filters
        }
      }
    );
    return data;
  };

  const openAdd = () => {
    setEditing(null);
    setOpenToken(token => token + 1);
    expenseModalRef.current.show();
  };

  const openEdit = (expense: OperatingExpense) => {
    if (isShopFundRow(expense) || !expense.workplace || !expense.category || !expense.paymentMethod) {
      return;
    }
    setEditing(expense);
    setOpenToken(token => token + 1);
    expenseModalRef.current.show();
  };

  const saveExpense = async () => {
    const saved = await formRef.current?.submitForm();
    if (saved) expenseModalRef.current.hide();
  };

  const voidExpense = async () => {
    if (!expenseToVoid || !branchDomain || isVoiding) return;
    if (!voidReason.trim()) {
      toast.error('A reason is required to void an expense.', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }

    try {
      setIsVoiding(true);
      await axiosInstance.post(
        `/api/v1/${branchDomain}/operating-expenses/${expenseToVoid.operatingExpenseId}/void`,
        { reason: voidReason.trim() }
      );
      toast.success('Expense voided.', { ...toastConfig, autoClose: 2000 });
      voidModalRef.current.hide();
      setExpenseToVoid(null);
      setVoidReason('');
      refresh();
    } catch (error) {
      toast.error(apiError(error, 'Could not void the expense.'), {
        ...toastConfig,
        autoClose: 3000
      });
    } finally {
      setIsVoiding(false);
    }
  };

  const columns: ColumnDef<OperatingExpense>[] = [
    {
      id: 'expenseDate',
      header: 'Date',
      accessorKey: 'expenseDate',
      cell: ({ row: { original } }) => (
        <span>{formatExpenseDate(original.expenseDate)}</span>
      )
    },
    {
      id: 'workplace',
      header: 'Store or shop',
      accessorKey: 'workplaceLabel',
      cell: ({ row: { original } }) => <span>{dash(original.workplaceLabel)}</span>
    },
    {
      id: 'category',
      header: 'Category',
      accessorKey: 'categoryLabel',
      cell: ({ row: { original } }) => <span>{dash(original.categoryLabel)}</span>
    },
    {
      id: 'payee',
      header: 'Payee',
      accessorKey: 'payee',
      cell: ({ row: { original } }) => <span>{dash(original.payee)}</span>
    },
    {
      id: 'description',
      header: 'Description',
      accessorKey: 'description',
      enableSorting: false,
      cell: ({ row: { original } }) => <span>{dash(original.description)}</span>
    },
    {
      id: 'amount',
      header: 'Amount',
      accessorKey: 'amount',
      cell: ({ row: { original } }) => (
        <span>{formatPesos(original.amount)}</span>
      )
    },
    {
      id: 'paymentMethod',
      header: 'Payment',
      accessorKey: 'paymentMethodLabel',
      cell: ({ row: { original } }) => <span>{dash(original.paymentMethodLabel)}</span>
    },
    {
      id: 'reference',
      header: 'Reference',
      accessorKey: 'reference',
      enableSorting: false,
      cell: ({ row: { original } }) => <span>{original.reference || '—'}</span>
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        if (original.isVoided) {
          return (
            <Badge bg="secondary" title={original.voidReason || undefined}>
              Voided
            </Badge>
          );
        }
        if (isShopFundRow(original)) {
          return (
            <Badge bg={original.fundDirection === 'Credit' ? 'success' : 'danger'}>
              {original.fundDirection === 'Credit' ? 'Credit' : 'Debit'}
            </Badge>
          );
        }
        return <Badge bg="success">Recorded</Badge>;
      }
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row: { original } }) =>
        original.isVoided || isShopFundRow(original) ? null : (
          <div className="d-flex gap-2">
            <Button
              variant="link"
              className="p-0"
              onClick={() => openEdit(original)}
            >
              Edit
            </Button>
            <Button
              variant="link"
              className="p-0 text-danger"
              onClick={() => {
                setExpenseToVoid(original);
                setVoidReason('');
                voidModalRef.current.show();
              }}
            >
              Void
            </Button>
          </div>
        )
    }
  ];

  const summary = summaryQuery.data;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Expenses</h4>
          <div className="text-body-tertiary">
            Money spent to run the store or the shop. Parts bought from
            suppliers stay on procurement invoices.
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="phoenix-secondary"
            onClick={() => {
              setUploadToken(token => token + 1);
              uploadModalRef.current.show();
            }}
          >
            Upload spreadsheet
          </Button>
          <Button variant="primary" onClick={openAdd}>
            Add expense
          </Button>
        </div>
      </div>

      {branchDomain ? (
        <FundsPanel
          branchDomain={branchDomain}
          refreshKey={refreshKey}
          onChanged={refresh}
        />
      ) : null}

      <div className="d-flex flex-wrap gap-2 mb-3">
        <div className="border rounded px-3 py-2 bg-body">
          <div className="fs-10 text-body-tertiary">Total</div>
          <div className="fw-bold fs-7">{formatPesos(summary?.totalAmount ?? 0)}</div>
        </div>
        {(summary?.categoryTotals ?? EXPENSE_CATEGORIES.map(item => ({
          category: item.value,
          categoryLabel: item.label,
          amount: 0
        }))).map(item => (
          <div key={item.category} className="border rounded px-3 py-2">
            <div className="fs-10 text-body-tertiary">{item.categoryLabel}</div>
            <div className="fw-semibold">{formatPesos(item.amount)}</div>
          </div>
        ))}
      </div>

      <Row className="g-2 mb-3">
        <Col md={3}>
          <Form.Select
            value={workplace}
            onChange={event => setWorkplace(event.target.value)}
          >
            <option value="">Store and shop</option>
            {EXPENSE_WORKPLACES.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select
            value={category}
            onChange={event => setCategory(event.target.value)}
          >
            <option value="">All categories</option>
            {EXPENSE_CATEGORIES.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <DatePicker
            options={{ mode: 'range', dateFormat: 'Y-m-d' }}
            placeholder="Date range"
            value={[dateFrom, dateTo]}
            onChange={selected => {
              const dates = selected.map(date => formatDateFilter(date));
              if (dates.length === 2) {
                setDateFrom(dates[0]);
                setDateTo(dates[1]);
              } else if (dates.length === 0) {
                setDateFrom('');
                setDateTo('');
              }
            }}
          />
        </Col>
      </Row>

      <DataTable
        queryKey={[
          'operating-expenses',
          branchDomain,
          workplace,
          category,
          dateFrom,
          dateTo,
          refreshKey
        ]}
        queryFn={fetchData}
        columns={columns}
        enabled={Boolean(branchDomain)}
        defaultOrderBy="expenseDate"
        defaultOrderByDesc
        getRowStyle={row => (row.isVoided ? { opacity: 0.55 } : {})}
        renderEmptyState={() => (
          <p className="text-center text-body-tertiary mt-4 mb-0">
            No expenses for this period.
          </p>
        )}
      />

      <CustomModal
        ref={expenseModalRef}
        title={editing ? 'Edit expense' : 'Add expense'}
        modalSize="lg"
        onSubmitCustomDisplay="Save"
        onSubmit={saveExpense}
        content={
          branchDomain ? (
            <ExpenseForm
              ref={formRef}
              branchDomain={branchDomain}
              expense={editing}
              openToken={openToken}
              refreshKey={refreshKey}
              onSaved={refresh}
            />
          ) : null
        }
      />

      <CustomModal
        ref={uploadModalRef}
        title="Upload expenses"
        modalSize="xl"
        hasCustomButtons
        content={
          branchDomain ? (
            <UploadExpensesForm
              key={uploadToken}
              branchDomain={branchDomain}
              onSaved={() => {
                uploadModalRef.current.hide();
                refresh();
              }}
            />
          ) : null
        }
      />

      <CustomModal
        ref={voidModalRef}
        title="Void expense"
        onSubmitCustomDisplay={isVoiding ? 'Voiding...' : 'Void'}
        onSubmit={voidExpense}
        content={
          <Form>
            <p>
              {expenseToVoid
                ? `${expenseToVoid.payee} · ${formatPesos(expenseToVoid.amount)}`
                : ''}
            </p>
            <Form.Label>Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={500}
              value={voidReason}
              onChange={event => setVoidReason(event.target.value)}
            />
            <div className="text-body-tertiary fs-9 mt-2">
              Voided expenses stay in the list and drop out of the totals.
            </div>
          </Form>
        }
      />
    </div>
  );
};

export default Expenses;
